import type { Capability } from '@viq-admin/ui';
import type { Action, AppAbility, Subject } from '../ability/superadmin';

/**
 * The console's capability encoding: "<action>:<Subject>".
 *
 * Every capability the console may reference must appear in CONSOLE_CAPABILITIES.
 * This registry is load-bearing, not documentation: `defineSuperAdminAbility`
 * grants `can('manage', 'all')` to super_admin, so CASL answers `true` for any
 * subject string at all — including typos. Validating against the registry
 * BEFORE delegating is what makes an unregistered key fail closed, matching the
 * portal predicate's behavior (see docs/superpowers/specs/2026-07-21-*).
 */

const SUBJECTS: Subject[] = [
  'Company',
  'Conference',
  'User',
  'Lead',
  'Toolkit',
  'Integration',
  'DiscoveryQuestion',
  // AI prompt store (Epic viq-backend#41) — PromptTemplateDetail's
  // editCap="manage:PromptTemplate" resolves through this registry, not CASL
  // directly, so it must be listed here too or the Edit button fails closed.
  // CorePrompt is added when the Core Prompts screen consumes it.
  'PromptTemplate',
];

const ACTIONS: Action[] = ['read', 'manage'];

export const CONSOLE_CAPABILITIES: ReadonlySet<Capability> = new Set(
  SUBJECTS.flatMap((s) => ACTIONS.map((a) => `${a}:${s}`)),
);

/**
 * Build the predicate injected into `<CapabilityProvider>`. Unregistered or
 * malformed keys are denied without consulting CASL.
 */
export function buildConsoleCan(
  ability: AppAbility,
): (cap: Capability) => boolean {
  return (cap: Capability) => {
    if (!CONSOLE_CAPABILITIES.has(cap)) return false;
    const [action, subject] = cap.split(':');
    return ability.can(action as Action, subject as Subject);
  };
}
